import {
    Button,
    Form,
    Input,
    InputNumber,
    message,
    Radio,
    Select,
    Space,
    Spin,
    Tooltip,
} from 'antd';
import * as React from 'react';
import Loader from '../common/Loader';
import {
    editAddress,
    saveAddress,
    generateTaskImageUrl,
} from '../../network/pickup.api';
import { StylesProps } from '../../theme/jss-types';
import {
    checkWhat3Word, getCities, getCountries, getStates, uploadImageToS3, getVehicleMakeList,
} from 'network/common.api';
import GenericHoc from '../common/generic-hoc';
import { ReduxStore } from '../../reducers/redux.types';
import { HocOptions } from '../common/generic-hoc.types';
import { useDebounce } from 'hooks/use-debounce';
import { get } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CustomerPortalConfig, LocationMasterConfig } from 'types/master-data-types';
import { backendToFormFieldMapping } from 'components/address-book/address-book.constants';
import MapLocationCapture from 'components/create-hyperlocal/MapLocationCapture';
import { EnvironmentOutlined } from '@ant-design/icons';
import TimeRangePickerList from './TimeRangePickerList';
import LocationImageUpload from './ImageUpload';
import GraceTimeInput from './GraceTimeInput';
import DockVehicleMapping, {
    DockMappingValue, dockMappingToApiArray, dockMappingFromApiArray,
} from './DockVehicleMapping';
import { getLocationImage, verifyNationalAddressCodeAndGetData } from './utils';

const styles = {
    newAddressItem: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
        marginRight: 8,
        width: '45%',
        '& .ant-form-item': {
            marginBottom: 0,
        },
    },
    newAddressItem2: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 16,
        marginRight: 8,
        width: '21.5%',
    },
    newAddressTitle: {
        color: '#333333',
        fontSize: 12,
        marginBottom: 4,
    },
    newAddressInput: {
        width: '100%',
    },
    newAddress: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'hidden',
    },
    addressRow: {
        display: 'flex',
        gap: '10px',
    },
    pincodeInput: {
        marginBottom: 0,
    },
    pincodeInputDropdown: {
        '& .ant-select-item-option-content': {
            whiteSpace: 'break-spaces',
            wordBreak: 'break-word',
        },
    },
    cityInput: {
        marginBottom: 0,
    },
    stateInput: {
        marginBottom: 0,
    },
    countryInput: {
        marginBottom: 0,
    },
    mobileInputCode: {
        height: 30,
        width: 50,
        margin: 0,
        padding: 0,
        '& .ant-input-group-addon .ant-select.ant-select-single:not(.ant-select-customize-input) .ant-select-selector': {
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& .ant-select-selector, ant-select-selection-item, ant-input-group-addon': {
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: '0px !important',
            paddingRight: '0px !important',
        },
    },
    pincodeWithExtra: {
        '& .ant-form-item-extra': {
            fontSize: 12,
            fontStyle: 'italic',
            marginBottom: 0,
            paddingBottom: 0,
        },
    },
    wrongW3W: {
        color: 'red',
    },
    stickySubmit: {
        height: '60px',
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
    },
};

const buttonStyle: React.CSSProperties = {
    fontWeight: 600,
    boxShadow: '2px 2px 4px 0 rgba(0,0,0,0.24)',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
};

const {
    useState,
    useEffect,
} = React;

const addressFormFields: any = {
    address_id: {
        required: true,
    },
    name: {
        required: true,
    },
    company_name: {
        required: false,
    },
    address_line_1: {
        required: true,
    },
    address_line_2: {
        required: false,
    },
    address_line_3: {
        required: false,
    },
    address_line_4: {
        required: false,
    },
    address_line_5: {
        required: false,
    },
    pincode: {
        required: true,
    },
    city: {
        required: true,
    },
    state: {
        required: true,
    },
    country: {
        required: false,
    },
    phone: {
        required: true,
    },
    alternate_phone: {
        required: false,
    },
    email: {
        required: false,
    },
    latitude: {
        required: false,
    },
    longitude: {
        required: false,
    },
    service_time: {
        required: false,
    },
    time_slot_list: {
        required: false,
    },
    location_image: {
        required: false,
    },
    grace_time: {
        required: false,
    },
    dock_capacity: {
        required: false,
    },
    dock_vehicle_mapping: {
        required: false,
    },
};

type NewAddress = {
    id?: string;
    pincode?: string;
    city?: string;
    state?: string;
    phone?: string;
    alternatePhone?: string;
    alternatePhone2?: string;
    notes?: string;
    address_line_1?: string;
    address_line_2?: string;
    address_line_3?: string;
    address_line_4?: string;
    address_line_5?: string;
    name?: string;
    country?: string;
    locationId?: string;
    locationCode?: string;
    email?: string;
    isInternational?: boolean;
    addressCode?: string;
    nationalAddressCode?: string;
    w3wCode?: string;
    latitude?: string;
    longitude?: string;
    companyName?: string;
    serviceTime?: number;
    graceTime?: number | string;
    dockCapacity?: number | string;
    timeSlotList?: any[];
    dockVehicleMapping?: DockMappingValue;
};

interface IProps extends StylesProps<typeof styles> {
    editData?: any;
    onClose: () => void;
    phoneRegex: any,
    isInternationalAddress: boolean;
    config: any;
    partsToShow: any;
    countryWisePhoneRegex: any[],
    updateFormData: any,
    showAddressType: boolean,
    customerPortalConfig: CustomerPortalConfig,
    showBackButton?: boolean,
    showSaveButtonAtBottom?: boolean,
    locationMasterConfig?: LocationMasterConfig,
}
const CreateAddress = (props: IProps) => {
    const {
        classes,
        editData,
        onClose,
        phoneRegex,
        isInternationalAddress,
        config,
        partsToShow,
        countryWisePhoneRegex,
        updateFormData,
        showAddressType,
        customerPortalConfig,
        showBackButton,
        showSaveButtonAtBottom,
        locationMasterConfig,
    } = props;

    const { t } = useTranslation();

    // Helper function to check if a field should be visible in the form
    // If no config available, show all fields (backward compatibility)
    const isFieldVisibleInForm = (backendKey: string): boolean => {
        if (!locationMasterConfig?.fields_list?.length) return true;
        return locationMasterConfig.fields_list.some((field) => field.key === backendKey);
    };

    const [creating, setCreating] = useState<boolean>(false);
    const [newAddress, setNewAddress] = React.useState<NewAddress>({});
    const [countriesListInternational, setCountriesListInternational] = React.useState<any[]>([]);
    const [statesList, setStatesList] = React.useState<any[]>([]);
    const [citiesList, setCitiesList] = React.useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [validPhone, setValidPhone] = useState<boolean | undefined>(true);
    const [validAlternatePhone, setValidAlternatePhone] = useState<boolean | undefined>(true);
    const [validAlternatePhone2, setValidAlternatePhone2] = useState<boolean | undefined>(true);
    const [validEmail, setValidEmail] = useState<boolean | undefined>(true);
    const emailRegex = customerPortalConfig?.email_validation_regex
        ? new RegExp(customerPortalConfig?.email_validation_regex)
        : /\S+@\S+\.\S+/;
    const enableAsn = partsToShow?.enable_advance_shipping_note_feature || false;
    const [phoneRegExp, setPhoneRegExp] = useState<RegExp>(new RegExp('^(.+)?$'.replace(/^\//, '').replace(/\/$/, '')));
    const [consignmentValidator, setConsignmentValidator] = useState<any>(addressFormFields);
    const [isWhat3Word, setIsWhat3Word] = React.useState<boolean | null>(true);
    // below state to track country change in international address
    const [currentCountry, setCurrentCountry] = useState<string>('');
    const [regexForPincode, setRegexForPincode] = useState<string>('');
    const [validPincode, setValidPincode] = useState<boolean | undefined>(true);
    const [samplePincode, setSamplePincode] = useState<string | undefined | null>(undefined);
    const [timeSlotKeys, setTimeSlotKeys] = useState<number[]>([0]);
    const [timeSlotValues, setTimeSlotValues] = useState<any>({});
    const [isInternational, setIsInternational] = useState<boolean>(
        editData?.id ? editData?.isInternational : isInternationalAddress,
    );
    const [countryCodeList, setCountryCodeList] = useState<any>(undefined);
    const [showMapForLocation, setShowMapForLocation] = useState<boolean>(false);
    const [locationImageFile, setLocationImageFile] = useState<any>([]);
    const [existingFile, setExistingFile] = useState<any>(editData?.locationImage);
    const [isNationalAddressCodeLoading, setIsNationalAddressCodeLoading] = useState<boolean>(false);
    const [vehicleMakeOptions, setVehicleMakeOptions] = useState<any[]>([]);
    const [isVehicleMakeLoading, setIsVehicleMakeLoading] = useState<boolean>(false);

    const showAddressCode = config?.customer_portal_config?.allow_address_code_in_addresses;

    const isWhat3WordEnableFlag = get(config,
        'customer_portal_consignment_config.enable_what3word_for_customer_portal', false) || false;

    const [form] = Form.useForm();

    const addressFieldConfig:any = config.address_fields_config || {};

    useEffect(() => {
        const modifiedValidator = { ...consignmentValidator };

        // First, apply address_fields_config if available (this can override location_master_config)
        if (Object.keys(addressFieldConfig).length > 0 && addressFieldConfig) {
            if (addressFieldConfig?.check_for_mandatory_address_fields) {
                Object.keys(modifiedValidator).forEach((item: any) => {
                    if (addressFieldConfig.mandatory_address_fields.length > 0) {
                        if (addressFieldConfig.mandatory_address_fields.includes(item)) {
                            modifiedValidator[item].required = true;
                        } else {
                            modifiedValidator[item].required = false;
                        }
                    } else {
                        modifiedValidator[item].required = false;
                    }
                });
            } else {
                Object.keys(modifiedValidator).forEach((item: any) => {
                    modifiedValidator[item].required = false;
                });
            }
        }

        // Then, apply location_master_config if available
        if (locationMasterConfig?.fields_list?.length) {
            locationMasterConfig.fields_list.forEach((fieldConfig) => {
                const formKey = backendToFormFieldMapping[fieldConfig.key];
                if (formKey && modifiedValidator[formKey]) {
                    // is_optional: false means required, is_optional: true means not required
                    modifiedValidator[formKey].required = fieldConfig.is_optional === false;
                }
            });
        }

        setConsignmentValidator(modifiedValidator);
    }, [locationMasterConfig]);

    const shouldFetchVehicleMakes = isFieldVisibleInForm('dock_vehicle_mapping');

    useEffect(() => {
        let isMounted = true;
        const fetchVehicleMakes = async () => {
            if (!shouldFetchVehicleMakes) {
                setVehicleMakeOptions([]);
                return;
            }
            setIsVehicleMakeLoading(true);
            const response = await getVehicleMakeList();
            if (!isMounted) return;
            if (response.isSuccess) {
                const list = Array.isArray(response.data) ? response.data : [];
                setVehicleMakeOptions(list.map((vehicleMake: { name?: string; code?: string }) => {
                    let label = '';
                    if (vehicleMake.name) {
                        label = vehicleMake.code ? `${vehicleMake.name} (${vehicleMake.code})` : vehicleMake.name;
                    } else {
                        label = vehicleMake.code || '';
                    }
                    return {
                        value: vehicleMake.code || vehicleMake.name || '',
                        label,
                    };
                }).filter((option: { value: string; label: string }) => option.value));
            } else if (response.errorMessage) {
                message.error(response.errorMessage);
            }
            setIsVehicleMakeLoading(false);
        };
        fetchVehicleMakes();
        return () => {
            isMounted = false;
        };
    }, [shouldFetchVehicleMakes]);

    const updatePhoneRegex = () => {
        let countryPhoneRegex;
        const country = newAddress.country;
        const countryPhoneRegexObj = countryWisePhoneRegex?.filter((elem) => elem.name === country || elem.code === country);
        if (countryPhoneRegexObj && countryPhoneRegexObj.length && countryPhoneRegexObj[0].phone_regex) {
            countryPhoneRegex = new RegExp(countryPhoneRegexObj[0].phone_regex.replace(/^\//, '').replace(/\/$/, ''));
        } else if (phoneRegex) {
            countryPhoneRegex = new RegExp(phoneRegex.replace(/^\//, '').replace(/\/$/, ''));
        }
        if (countryPhoneRegex) {
            setPhoneRegExp(countryPhoneRegex);
        }
    };

    const updateAddressFieldWithValidations = (key: string, value: string) => {
        if (key === 'country') {
            let countryPhoneRegex;
            const countryPhoneRegexObj = countryWisePhoneRegex?.filter((elem) => elem.name === value || elem.code === value);
            if (countryPhoneRegexObj && countryPhoneRegexObj.length && countryPhoneRegexObj[0].phone_regex) {
                countryPhoneRegex = new RegExp(countryPhoneRegexObj[0].phone_regex.replace(/^\//, '').replace(/\/$/, ''));
            } else if (phoneRegex) {
                countryPhoneRegex = new RegExp(phoneRegex.replace(/^\//, '').replace(/\/$/, ''));
            }
            if (countryPhoneRegex) {
                setPhoneRegExp(countryPhoneRegex);
            }
        }
        setNewAddress({
            ...newAddress,
            [key]: value,
        });
    };

    const debounceW3WCodeString = useDebounce(newAddress.w3wCode);

    const handleVerifyNationalAddressCode = async () => {
        if (!newAddress?.nationalAddressCode || !newAddress.nationalAddressCode.trim()) {
            message.warning(t('Please enter a national address code'));
            return;
        }

        setIsNationalAddressCodeLoading(true);
        try {
            const addressData = await verifyNationalAddressCodeAndGetData(
                newAddress.nationalAddressCode,
                'address',
            );

            if (addressData) {
                setNewAddress({
                    ...newAddress,
                    ...addressData,
                });
                form.setFieldsValue({
                    ...addressData,
                });
                message.success(t('Verification successful'));
            } else {
                setNewAddress({
                    ...newAddress,
                    nationalAddressCode: '',
                });
                form.setFieldsValue({
                    nationalAddressCode: '',
                });
                message.error(t('Verification failed'));
            }
        } catch (error) {
            message.error(t('Error verifying national address code'));
        } finally {
            setIsNationalAddressCodeLoading(false);
        }
    };

    const checkW3WCode = async () => {
        setLoading(true);

        if ((!newAddress?.w3wCode) || newAddress?.w3wCode?.length === 0) {
            setIsWhat3Word(true);
            setLoading(false);
            return;
        }

        const response = await checkWhat3Word({
            w3wcode: newAddress.w3wCode,
        });

        if (response?.data?.locationData?.length) {
            const locationData = response?.data?.locationData[0];
            if (locationData.success) {
                setIsWhat3Word(true);
            } else {
                setIsWhat3Word(false);
            }
        } else {
            setIsWhat3Word(false);
        }

        setLoading(false);
    };

    const loadCountries = async () => {
        setLoading(true);
        const response = await getCountries();
        setCountriesListInternational(response?.data);
        setLoading(false);
    };

    const loadStates = async () => {
        setLoading(true);
        const response = await getStates({
            stateName: '',
            countryName: newAddress.country,
        });
        setStatesList(response?.data);
        setLoading(false);
    };

    const loadCities = async (city: string) => {
        if (!city || city.length === 0 || !newAddress.country) {
            setCitiesList([]);
            return;
        }
        setLoading(true);
        const response = await getCities({
            countryName: newAddress.country,
            cityName: city,
        });
        setCitiesList(response?.data);
        setLoading(false);
    };


    useEffect(() => {
        if (phoneRegex) {
            const phoneRegexToSet = new RegExp(phoneRegex.replace(/^\//, '').replace(/\/$/, ''));
            setPhoneRegExp(phoneRegexToSet);
        }
        if (editData?.id) {
            const locality = {
                city: editData.cityName,
                state: editData.stateName,
                country: editData.countryName,
                address_line_1: editData.addressLine1,
                address_line_2: editData.addressLine2,
                address_line_3: editData.addressLine3,
                address_line_4: editData.addressLine4,
                address_line_5: editData.addressLine5,
                w3wCode: editData.w3wCode,
                nationalAddressCode: editData.nationalAddressCode || editData.national_address_code,
            };
            setNewAddress({
                ...editData,
                ...locality,
                dockVehicleMapping: Array.isArray(editData.dockVehicleMapping)
                    ? dockMappingFromApiArray(editData.dockVehicleMapping)
                    : editData.dockVehicleMapping,
            });
            form.setFieldsValue({ ...editData, ...locality });
            form.validateFields();
        }
        if (isInternational) {
            loadCountries();
        }
    }, []);

    useEffect(() => {
        if (isInternational) {
            loadCountries();
        } else {
            setSamplePincode(undefined);
            setRegexForPincode('');
        }
        if (!editData?.id) {
            setNewAddress({
                ...newAddress,
                city: '',
                state: '',
                country: '',
                pincode: '',
            });
            form.setFieldsValue({
                pincode: '',
                city: '',
                state: '',
                country: '',
            });
        }
        form.validateFields();
        if (!newAddress?.country) setStatesList([]);
    }, [isInternational]);

    useEffect(() => {
        if (isInternational && newAddress.country) {
            setNewAddress({
                ...newAddress,
                city: '',
                state: '',
            });
            setCitiesList([]);
            loadStates();
        }
    }, [currentCountry]);

    useEffect(() => {
        updatePhoneRegex();
        if (isInternational && newAddress.country) {
            loadStates();
        }
    }, [newAddress.country]);

    useEffect(() => {
        if (isInternational && newAddress.country && countriesListInternational) {
            countriesListInternational.forEach((country: any) => {
                if (newAddress?.country === country?.country) {
                    setRegexForPincode(country?.pincode_regex);
                    setSamplePincode(country?.sample_pincode);
                    setCountryCodeList(country?.phoneValidations?.map((validation: any) => {
                        return {
                            label: validation.code,
                            value: validation.code,
                        };
                    }));
                    let phoneRegexToSet = new RegExp(
                        country?.phoneValidations?.[0]?.lengthRegex?.replace(/^\//, '').replace(/\/$/, ''),
                    );

                    form.setFieldsValue({
                        countryCode_alternatePhone: country?.phoneValidations?.[0]?.code,
                        countryCode_alternatePhone2: country?.phoneValidations?.[0]?.code,
                        countryCode_phone: country?.phoneValidations?.[0]?.code,
                    });

                    country?.phoneValidations?.forEach((phoneValidation: any) => {
                        if (newAddress?.phone?.startsWith(phoneValidation.code)) {
                            const phone = newAddress?.phone?.replace(phoneValidation.code, '');
                            setNewAddress({
                                ...newAddress,
                                phone,
                            });
                            form.setFieldsValue({ phone });
                            phoneRegexToSet = new RegExp(
                                phoneValidation?.lengthRegex?.replace(/^\//, '').replace(/\/$/, ''),
                            );
                        }
                        if (newAddress?.alternatePhone?.startsWith(phoneValidation.code)) {
                            const alternatePhone = newAddress?.alternatePhone?.replace(phoneValidation.code, '');
                            setNewAddress({
                                ...newAddress,
                                alternatePhone,
                            });
                            form.setFieldsValue({ alternatePhone });
                            phoneRegexToSet = new RegExp(
                                phoneValidation?.lengthRegex?.replace(/^\//, '').replace(/\/$/, ''),
                            );
                        }
                        if (newAddress?.alternatePhone2?.startsWith(phoneValidation.code)) {
                            const alternatePhone2 = newAddress?.alternatePhone2?.replace(phoneValidation.code, '');
                            setNewAddress({
                                ...newAddress,
                                alternatePhone2,
                            });
                            form.setFieldsValue({ alternatePhone2 });
                        }
                    });
                    setPhoneRegExp(phoneRegexToSet);
                }
            });
        }
    }, [newAddress.country, countriesListInternational]);

    useEffect(() => {
        if (isInternational) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const isValid = isValidPincode(newAddress?.pincode);
            if (isValid !== validPincode) {
                setValidPincode(isValid);
            }
        }
    }, [regexForPincode]);

    useEffect(() => {
        form.validateFields();
    }, [phoneRegExp]);

    useEffect(() => {
        checkW3WCode();
    }, [debounceW3WCodeString]);

    const isEditingPage = () => {
        if (editData?.id) return true;
        return false;
    };

    const getTimeSlotList = () => {
        const timeSlots = timeSlotKeys?.map((timeSlotKey) => {
            const value = timeSlotValues[timeSlotKey];

            if (value?.length === 2) {
                const timeSlotStart = value[0]?.format('HH:mm');
                const timeSlotEnd = value[1]?.format('HH:mm');
                return [timeSlotStart, timeSlotEnd];
            }

            return undefined;
        });

        const validTimeSlots = timeSlots?.filter((slot) => slot !== undefined);
        return validTimeSlots;
    };

    const createAddress = async () => {
        // Validate dock capacity and dock vehicle mapping compatibility
        const dockCapacityNum = parseFloat(newAddress.dockCapacity as any);

        if (isFieldVisibleInForm('dock_vehicle_mapping') && newAddress.dockVehicleMapping) {
            const mappingValue = newAddress.dockVehicleMapping;
            const totalMappedDocks = Object.keys(mappingValue).length;

            if (dockCapacityNum && totalMappedDocks > dockCapacityNum) {
                message.error(t('Number of mapped docks cannot exceed dock capacity'));
                setCreating(false);
                return;
            }
        }

        const locationImageUrl = await getLocationImage(locationImageFile, generateTaskImageUrl, uploadImageToS3);
        if (Array.isArray(locationImageFile) && locationImageFile.length > 0 && !locationImageUrl) {
            setCreating(false);
            return;
        }
        setCreating(true);
        const apiBody: any = {
            id: editData?.id,
            addressLine1: newAddress.address_line_1,
            addressLine2: newAddress.address_line_2,
            addressLine3: newAddress.address_line_3,
            addressLine4: newAddress.address_line_4,
            addressLine5: newAddress.address_line_5,
            cityName: newAddress.city,
            isInternational: isInternational || false,
            name: newAddress.name,
            phone: newAddress.phone && isInternational
                ? `${form.getFieldValue('countryCode_phone') || ''}${newAddress.phone}`
                : newAddress.phone,
            alternatePhone: newAddress.alternatePhone && isInternational
                ? `${form.getFieldValue('countryCode_alternatePhone') || ''}${newAddress.alternatePhone}`
                : newAddress.alternatePhone,
            alternatePhone2: newAddress.alternatePhone2 && isInternational
                ? `${form.getFieldValue('countryCode_alternatePhone2') || ''}${newAddress.alternatePhone2}`
                : newAddress.alternatePhone2,
            notes: newAddress.notes,
            pincode: newAddress.pincode,
            stateName: newAddress.state,
            countryName: newAddress.country,
            locationId: enableAsn ? newAddress.locationId : undefined,
            email: newAddress.email,
            companyName: newAddress.companyName,
            addressCode: newAddress.addressCode,
            nationalAddressCode: newAddress.nationalAddressCode,
            w3wCode: newAddress.w3wCode,
            serviceTime: newAddress.serviceTime,
            locationImage: (isEditingPage() && existingFile === null)
                ? '' : locationImageUrl,
            latitude: newAddress.latitude?.toString(),
            longitude: newAddress.longitude?.toString(),
            timeSlotList: getTimeSlotList(),
            graceTime: newAddress.graceTime,
            dockCapacity: newAddress.dockCapacity,
            dockVehicleMapping: newAddress.dockVehicleMapping
                ? dockMappingToApiArray(newAddress.dockVehicleMapping)
                : undefined,
        };

        const customerCode = window.localStorage.getItem('userCode');
        apiBody.customerCode = customerCode || '';
        if (newAddress.locationId) {
            apiBody.locationCode = customerCode ? `${customerCode}_${newAddress.locationId}` : '';
        }
        const isPluginAddress = ['oldDstAddress', 'oldSrcAddress'].includes(editData?.id);

        if (isPluginAddress) {
            updateFormData(apiBody);
            setCreating(false);
            onClose();
            return;
        }
        if (editData?.id) {
            const response = await editAddress(apiBody);
            setCreating(false);
            if (response.isSuccess) {
                message.success('Address successfully edited');
                if (updateFormData) {
                    updateFormData(apiBody);
                }
                onClose();
            } else {
                message.error(response.errorMessage);
            }
            return;
        }
        const response = await saveAddress(apiBody);
        setCreating(false);
        if (response?.isSuccess) {
            message.success('Address successfully saved');
            if (updateFormData) {
                updateFormData(apiBody);
            }
            onClose();
        } else {
            message.error(response.errorMessage);
        }
    };

    const updateAddressField = (key: string, value: string | undefined) => {
        if (key === 'country') {
            let countryPhoneRegex;
            const countryPhoneRegexObj = countryWisePhoneRegex?.filter((elem) => elem.name === value || elem.code === value);
            if (countryPhoneRegexObj && countryPhoneRegexObj.length && countryPhoneRegexObj[0].phone_regex) {
                countryPhoneRegex = new RegExp(countryPhoneRegexObj[0].phone_regex.replace(/^\//, '').replace(/\/$/, ''));
            } else if (phoneRegex) {
                countryPhoneRegex = new RegExp(phoneRegex.replace(/^\//, '').replace(/\/$/, ''));
            }
            if (countryPhoneRegex) {
                setPhoneRegExp(countryPhoneRegex);
            }
        }
        setNewAddress({
            ...newAddress,
            [key]: value,
        });
    };

    const isValidPhoneNumber = (num: string | undefined) => {
        if (isInternational && (Boolean(newAddress?.country) !== Boolean(countryCodeList))) return true;
        if (phoneRegExp === null || !num) return true;
        return phoneRegExp?.test(num);
    };

    const isValidEmail = (email: string | undefined) => {
        if (!email) return true;
        return emailRegex.test(email);
    };

    const isValidPincode = (pincode: string | undefined) => {
        if (!regexForPincode || !pincode) return true;
        const re = new RegExp(regexForPincode?.replace(/^\//, '')?.replace(/\/$/, ''));
        return re.test(pincode);
    };

    const textValidity = (field : any) => {
        if (field.value) {
            if (field.value.length >= 3) {
                return true;
            }
        } else if (!field.isMandatory) {
            return true;
        }
        return false;
    };

    const isValidTextFields = (addressToValidate: any) => {
        const fieldsToValidate = [
            {
                value: addressToValidate?.name,
                isMandatory: consignmentValidator.name.required,
            },
            {
                value: addressToValidate?.address_line_1,
                isMandatory: consignmentValidator.address_line_1.required,
            },
            {
                value: addressToValidate?.address_line_2,
                isMandatory: consignmentValidator.address_line_2.required,
            },
        ];

        return fieldsToValidate.every(textValidity);
    };

    const renderName = () => {
        if (!isFieldVisibleInForm('name')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_name')}
                    {consignmentValidator.name.required ? '*' : ''}
                </span>
                <Input
                    value={newAddress.name}
                    className={classes.newAddressInput}
                    placeholder={t('address_name')}
                    onChange={(e) => updateAddressFieldWithValidations('name', e.target.value)}
                />
            </div>
        );
    };

    const renderClientName = () => {
        if (!isFieldVisibleInForm('company_name')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('company_name')}
                    {consignmentValidator.company_name.required ? '*' : ''}
                </span>
                <Input
                    value={newAddress.companyName}
                    className={classes.newAddressInput}
                    placeholder={t('company_name')}
                    onChange={(e) => updateAddressFieldWithValidations('companyName', e.target.value)}
                />
            </div>
        );
    };

    const renderAddressCode = () => {
        if (!showAddressCode || !isFieldVisibleInForm('address_id')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_code')}
                    *
                </span>
                <Input
                    value={newAddress.addressCode}
                    disabled={isEditingPage()}
                    className={classes.newAddressInput}
                    placeholder={t('address_code_placeholder')}
                    onChange={(e) => updateAddressFieldWithValidations('addressCode', e.target.value)}
                />
            </div>
        );
    };

    const renderNationalAddressCode = () => {
        const { Search } = Input;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('national_address_code')}
                </span>
                <Search
                    value={newAddress.nationalAddressCode}
                    className={classes.newAddressInput}
                    placeholder={t('national_address_code_placeholder')}
                    enterButton={isNationalAddressCodeLoading ? <Spin size="small" /> : t('verify')}
                    loading={isNationalAddressCodeLoading}
                    onSearch={handleVerifyNationalAddressCode}
                    onChange={(e) => updateAddressFieldWithValidations('nationalAddressCode', e.target.value)}
                />
            </div>
        );
    };

    const renderAddressLine2 = () => {
        if (!isFieldVisibleInForm('address_line_2')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_line_2')}
                    {consignmentValidator.address_line_2.required ? '*' : ''}
                </span>
                <Input
                    value={newAddress.address_line_2}
                    className={classes.newAddressInput}
                    placeholder={t('address_line_2')}
                    onChange={(e) => updateAddressFieldWithValidations('address_line_2', e.target.value)}
                />
            </div>
        );
    };

    const renderAddressLine3 = () => {
        if (!isFieldVisibleInForm('address_line_3')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_line_3')}
                </span>
                <Input
                    value={newAddress.address_line_3}
                    className={classes.newAddressInput}
                    placeholder={t('address_line_3')}
                    onChange={(e) => updateAddressFieldWithValidations('address_line_3', e.target.value)}
                />
            </div>
        );
    };

    const renderAddressLine4 = () => {
        if (!isFieldVisibleInForm('address_line_4')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_line_4')}
                </span>
                <Input
                    value={newAddress.address_line_4}
                    className={classes.newAddressInput}
                    placeholder={t('address_line_4')}
                    onChange={(e) => updateAddressFieldWithValidations('address_line_4', e.target.value)}
                />
            </div>
        );
    };

    const renderAddressLine5 = () => {
        if (!isFieldVisibleInForm('address_line_5')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_line_5')}
                </span>
                <Input
                    value={newAddress.address_line_5}
                    className={classes.newAddressInput}
                    placeholder={t('address_line_5')}
                    onChange={(e) => updateAddressFieldWithValidations('address_line_5', e.target.value)}
                />
            </div>
        );
    };

    const renderAddressLine1 = () => {
        if (!isFieldVisibleInForm('address_line_1')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_line_1')}
                    {consignmentValidator.address_line_1.required ? '*' : ''}
                </span>
                <Input
                    value={newAddress.address_line_1}
                    className={classes.newAddressInput}
                    placeholder={t('address_line_1')}
                    onChange={(e) => updateAddressFieldWithValidations('address_line_1', e.target.value)}
                />
            </div>
        );
    };

    const renderAddressRow = () => {
        return (
            <div className={classes.addressRow}>
                {renderAddressLine1()}
                {renderAddressLine2()}
                {renderAddressLine3()}
                {renderAddressLine4()}
                {renderAddressLine5()}
            </div>
        );
    };

    const renderPincodeBox = () => {
        return (
            <Form.Item
                className={classes.pincodeWithExtra}
                name="pincode"
                extra={samplePincode ? `Ex. ${samplePincode}` : undefined}
            >
                <Input
                    value={newAddress.pincode}
                    className={classes.pincodeInput}
                    placeholder={t('address_pincode')}
                    onChange={(e) => {
                        let pincodeValue = e.target.value || '';
                        pincodeValue = pincodeValue.toString().trim();
                        updateAddressFieldWithValidations('pincode', pincodeValue);
                        const isValid = isValidPincode(pincodeValue);
                        if (isValid !== validPincode) {
                            setValidPincode(isValid);
                        }
                    }}
                    style={validPincode ? {} : { borderColor: 'red' }}
                />
            </Form.Item>
        );
    };

    const renderPincode = () => {
        if (!isFieldVisibleInForm('pincode')) return null;
        return (
            <div className={classes.newAddressItem2}>
                <span className={classes.newAddressTitle}>
                    {t('address_pincode')}
                    {consignmentValidator.pincode.required && !isInternational ? '*' : ''}
                </span>
                {renderPincodeBox()}
            </div>
        );
    };

    const renderCityBox = () => {
        return (
            <Input
                value={newAddress.city}
                className={classes.cityInput}
                placeholder={t('address_city')}
                onChange={(e) => updateAddressFieldWithValidations('city', e.target.value)}
            />
        );
    };

    const renderCity = () => {
        if (!isFieldVisibleInForm('city')) return null;
        return (
            <div className={classes.newAddressItem2}>
                <span className={classes.newAddressTitle}>
                    {t('address_city')}
                    {consignmentValidator.city.required ? '*' : ''}
                </span>
                { isInternational
                    ? (
                        <Select
                            value={newAddress.city}
                            className={classes.cityInput}
                            placeholder={t('address_city')}
                            onChange={(e) => updateAddressField('city', e)}
                            options={
                                citiesList.map((city) => {
                                    return {
                                        label: city.city_name,
                                        value: city.city_name,
                                    };
                                })
                            }
                            defaultActiveFirstOption={false}
                            filterOption={false}
                            onSearch={loadCities}
                            notFoundContent={null}
                            showSearch
                        />
                    ) : (renderCityBox()) }
            </div>
        );
    };

    const renderState = () => {
        if (!isFieldVisibleInForm('state')) return null;
        return (
            <div className={classes.newAddressItem2}>
                <span className={classes.newAddressTitle}>
                    {t('address_state')}
                    {consignmentValidator.state.required && !isInternational ? '*' : ''}
                </span>
                { isInternational
                    ? (
                        <Select
                            value={newAddress.state}
                            className={classes.stateInput}
                            placeholder={t('address_state')}
                            onChange={(e) => updateAddressFieldWithValidations('state', e)}
                            showSearch
                        >
                            {
                                statesList.map((state) => {
                                    return (
                                        <Select.Option
                                            value={state.state_name}
                                            // eslint-disable-next-line no-underscore-dangle
                                            key={`${state.state_name}-${state._id}`}
                                        >
                                            {state.state_name}
                                        </Select.Option>
                                    );
                                })
                            }
                        </Select>
                    ) : (
                        <Input
                            // disabled
                            value={newAddress.state}
                            className={classes.stateInput}
                            placeholder={t('address_state')}
                            onChange={(e) => updateAddressFieldWithValidations('state', e.target.value)}
                        />
                    )}
            </div>
        );
    };

    const renderCountryBox = () => {
        return (
            <Input
                value={newAddress.country}
                className={classes.countryInput}
                placeholder={t('address_country')}
                onChange={(e) => updateAddressFieldWithValidations('country', e.target.value)}
            />
        );
    };

    const renderCountry = () => {
        if (!isFieldVisibleInForm('country')) return null;
        return (
            <div className={classes.newAddressItem2}>
                <span className={classes.newAddressTitle}>
                    {t('address_country')}
                    {consignmentValidator.country.required || isInternational ? '*' : ''}
                </span>
                { isInternational
                    ? (
                        <Select
                            value={newAddress.country}
                            className={classes.countryInput}
                            placeholder={t('address_country')}
                            onChange={(e) => {
                                updateAddressFieldWithValidations('country', e);
                                setCurrentCountry(e);
                            }}
                            options={
                                countriesListInternational.map((country) => {
                                    return {
                                        label: country.country,
                                        value: country.country,
                                    };
                                })
                            }
                            showSearch
                        />
                    ) : (renderCountryBox())}
            </div>
        );
    };

    const renderLocality = () => {
        if (isInternational) {
            return (
                <div className={classes.addressRow}>
                    {renderCountry()}
                    {renderState()}
                    {renderCity()}
                    {renderPincode()}
                </div>
            );
        }
        return (
            <div className={classes.addressRow}>
                {renderPincode()}
                {renderCity()}
                {renderState()}
                {renderCountry()}
            </div>
        );
    };

    const renderCountryCode = (type: string) => {
        if (!countryCodeList) return null;
        if (!isInternational) return null;
        return (
            <Form.Item
                name={`countryCode_${type}`}
                className={classes.mobileInputCode}
                initialValue={countryCodeList[0].value}
            >
                <Select
                    allowClear={false}
                    showArrow={false}
                    options={countryCodeList}
                    style={{ width: '100%' }}
                />
            </Form.Item>
        );
    };

    const renderPhoneNumber = () => {
        if (!isFieldVisibleInForm('phone')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('mobile_number')}
                    {consignmentValidator.phone.required ? '*' : ''}
                </span>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    name="phone"
                    rules={[{
                        validator: (rule, value, cb) => {
                            if (!isValidPhoneNumber(value)) {
                                return cb('Invalid phone no.');
                            }
                            return cb();
                        },
                    }]}
                >
                    <Input
                        style={validPhone ? {} : { borderColor: 'red' }}
                        className={classes.newAddressInput}
                        placeholder={t('mobile_number')}
                        value={newAddress.phone}
                        addonBefore={renderCountryCode('phone')}
                        onChange={
                            (e) => {
                                updateAddressFieldWithValidations('phone', e.target.value);
                                const isValid = isValidPhoneNumber(e.target.value);
                                if (isValid !== validPhone) {
                                    setValidPhone(isValid);
                                }
                            }
                        }
                    />
                </Form.Item>
            </div>
        );
    };

    const renderAlternatePhone = () => {
        if (!isFieldVisibleInForm('alternate_phone')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('alternate_mobile_number')}
                    {consignmentValidator.alternate_phone?.required ? '*' : ''}
                </span>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    name="alternatePhone"
                    rules={[{
                        validator: (rule, value, cb) => {
                            if (!isValidPhoneNumber(value)) {
                                return cb('Invalid phone no.');
                            }
                            return cb();
                        },
                    }]}
                >
                    <Input
                        className={classes.newAddressInput}
                        placeholder={t('alternate_mobile_number')}
                        addonBefore={renderCountryCode('alternatePhone')}
                        value={newAddress.alternatePhone}
                        onChange={
                            (e) => {
                                updateAddressFieldWithValidations('alternatePhone', e.target.value);
                                const isValid = isValidPhoneNumber(e.target.value);
                                if (isValid !== validAlternatePhone) {
                                    setValidAlternatePhone(isValid);
                                }
                            }
                        }
                        style={validAlternatePhone ? {} : { borderColor: 'red' }}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderAlternatePhone2 = () => {
        if (!isFieldVisibleInForm('alternate_phone_2')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('alternatePhone2')}
                </span>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    name="alternatePhone2"
                    rules={[{
                        validator: (rule, value, cb) => {
                            if (!isValidPhoneNumber(value)) {
                                return cb('Invalid phone no.');
                            }
                            return cb();
                        },
                    }]}
                >
                    <Input
                        className={classes.newAddressInput}
                        placeholder={t('alternatePhone2')}
                        addonBefore={renderCountryCode('alternatePhone2')}
                        value={newAddress.alternatePhone2}
                        onChange={
                            (e) => {
                                updateAddressFieldWithValidations('alternatePhone2', e.target.value);
                                const isValid = isValidPhoneNumber(e.target.value);
                                if (isValid !== validAlternatePhone2) {
                                    setValidAlternatePhone2(isValid);
                                }
                            }
                        }
                        style={validAlternatePhone2 ? {} : { borderColor: 'red' }}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderNotes = () => {
        if (!isFieldVisibleInForm('notes')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('notes')}
                </span>
                <Form.Item
                    style={{ marginBottom: 0 }}
                    name="notes"
                >
                    <Input
                        className={classes.newAddressInput}
                        placeholder={t('notes')}
                        value={newAddress.notes}
                        onChange={(e) => updateAddressField('notes', e.target.value)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderLocationId = () => {
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    Location ID*
                </span>
                <Input
                    value={newAddress.locationId}
                    className={classes.newAddressInput}
                    placeholder="Location ID"
                    onChange={(e) => updateAddressField('locationId', e.target.value)}
                />
            </div>
        );
    };

    const renderTimeSlot = () => {
        if (!isFieldVisibleInForm('time_slot_list')) return null;
        return (
            <Form form={form} style={{ width: '45%' }}>
                <TimeRangePickerList
                    form={form}
                    format="HH:mm"
                    label="time_slot"
                    required={consignmentValidator.time_slot_list?.required}
                    timeSlotList={editData?.timeSlotList}
                    timeSlotKeys={timeSlotKeys}
                    timeSlotValues={timeSlotValues}
                    setTimeSlotKeys={setTimeSlotKeys}
                    setTimeSlotValues={setTimeSlotValues}
                    disabled={false}
                />
            </Form>
        );
    };

    const renderServiceTime = () => {
        if (!isFieldVisibleInForm('service_time')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('serviceTime')}
                    {consignmentValidator.service_time?.required ? '*' : ''}
                </span>
                <Input
                    value={newAddress.serviceTime}
                    className={classes.newAddressInput}
                    placeholder={t('serviceTime')}
                    onChange={(e) => updateAddressFieldWithValidations('serviceTime', e.target.value)}
                />
            </div>
        );
    };

    const renderLocationImage = () => {
        if (!isFieldVisibleInForm('location_image')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    Location Picture
                    {consignmentValidator.location_image?.required ? '*' : ''}
                </span>
                <LocationImageUpload
                    fileList={locationImageFile}
                    setFileList={setLocationImageFile}
                    disabled={creating || loading}
                    maxFileSizeMB={10}
                    existingFile={existingFile}
                    setExistingFile={setExistingFile}
                />
            </div>
        );
    };

    const renderServiceTimeAndLocationImage = () => {
        return (
            <div className={classes.addressRow}>
                {renderServiceTime()}
                {renderLocationImage()}
            </div>
        );
    };


    const renderEmail = () => {
        if (!isFieldVisibleInForm('email')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_email')}
                    {consignmentValidator.email?.required || isInternational ? '*' : ''}
                </span>
                <Input
                    className={classes.newAddressInput}
                    placeholder={t('address_email')}
                    value={newAddress.email}
                    type="email"
                    onChange={
                        (e) => {
                            updateAddressField('email', e.target.value);
                            const isValid = isValidEmail(e.target.value);
                            if (isValid !== validEmail) {
                                setValidEmail(isValid);
                            }
                        }
                    }
                    style={validEmail ? {} : { borderColor: 'red' }}
                />
            </div>
        );
    };

    const renderPhoneNumbers = () => {
        return (
            <div className={classes.addressRow}>
                {renderPhoneNumber()}
                {renderAlternatePhone()}
                {renderAlternatePhone2()}
                {renderNotes()}
            </div>
        );
    };

    const renderWhat3Word = () => {
        if (isWhat3WordEnableFlag) {
            return (
                <div className={classes.newAddressItem}>
                    <span className={classes.newAddressTitle}>
                        What3Word Code
                    </span>
                    <div>
                        <Input
                            className={classes.newAddressInput}
                            placeholder="Eg. fizzle.clip.quota"
                            value={newAddress.w3wCode}
                            onChange={
                                (e) => {
                                    updateAddressFieldWithValidations('w3wCode', e.target.value);
                                }
                            }
                        />
                        <div className={classes.wrongW3W}>
                            {!isWhat3Word ? <p>please enter a valid what3word code</p> : null }
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    const btnDisabled = () => {
        // Check for dock capacity and dock vehicle mapping compatibility
        const dockCapacityNum = parseFloat(newAddress.dockCapacity as any);

        let isDockMappingInvalid = false;
        if (isFieldVisibleInForm('dock_vehicle_mapping')) {
            if (newAddress.dockVehicleMapping) {
                const totalMappedDocks = Object.keys(newAddress.dockVehicleMapping).length;
                isDockMappingInvalid = dockCapacityNum ? totalMappedDocks > dockCapacityNum : false;
            }
            // Check if dock vehicle mapping is required but not provided
            if (consignmentValidator.dock_vehicle_mapping?.required && !newAddress.dockVehicleMapping) {
                isDockMappingInvalid = true;
            }
        }

        // Check if dock capacity is invalid (negative or zero when present)
        let isDockCapacityInvalid = dockCapacityNum !== undefined
            && dockCapacityNum !== null
            && !Number.isNaN(dockCapacityNum)
            && dockCapacityNum <= 0;
        if (consignmentValidator.dock_capacity?.required
            && (newAddress.dockCapacity === undefined
                || newAddress.dockCapacity === null
                || newAddress.dockCapacity === '')) {
            isDockCapacityInvalid = true;
        }

        return (
            (consignmentValidator.name?.required && !newAddress?.name)
            || (consignmentValidator.address_line_1?.required && !newAddress.address_line_1)
            || (consignmentValidator.address_line_2?.required && !newAddress.address_line_2)
            || (consignmentValidator.pincode?.required && (!newAddress.pincode && !isInternational))
            || (consignmentValidator.state?.required && (!newAddress.state && !isInternational))
            || (consignmentValidator.city?.required && !newAddress.city)
            || (consignmentValidator.phone?.required && !newAddress.phone)
            || (consignmentValidator.country?.required && !newAddress.country)
            || (consignmentValidator.company_name?.required && !newAddress.companyName)
            || (consignmentValidator.email?.required && !newAddress.email)
            || (consignmentValidator.alternate_phone?.required && !newAddress.alternatePhone)
            || (consignmentValidator.latitude?.required && !newAddress.latitude)
            || (consignmentValidator.longitude?.required && !newAddress.longitude)
            || (consignmentValidator.service_time?.required && !newAddress.serviceTime)
            || (consignmentValidator.grace_time?.required
                && (newAddress.graceTime === undefined
                    || newAddress.graceTime === null
                    || newAddress.graceTime === ''))
            || (consignmentValidator.time_slot_list?.required
                && (!timeSlotValues || Object.keys(timeSlotValues).length === 0))
            || (isInternational && !newAddress.email)
            || (showAddressCode && !newAddress.addressCode)
            || (enableAsn && !newAddress.locationId)
            || !isValidPhoneNumber(newAddress.phone)
            || !isValidPhoneNumber(newAddress.alternatePhone)
            || !isValidPhoneNumber(newAddress.alternatePhone2)
            || !isValidEmail(newAddress.email)
            || (isInternational && !newAddress.country)
            || !isValidTextFields(newAddress)
            || (!isWhat3Word)
            || !isValidPincode(newAddress.pincode)
            || isDockCapacityInvalid
            || isDockMappingInvalid
        );
    };

    const renderSubmit = () => {
        return (
            <div className={classes.stickySubmit}>
                {showBackButton ? (
                    <Button onClick={onClose} style={{ marginRight: '10px' }}>
                        {t('cancel')}
                    </Button>
                ) : null}
                <Button
                    type="primary"
                    loading={creating}
                    onClick={createAddress}
                    disabled={btnDisabled()}
                    style={buttonStyle}
                >
                    {t('save')}
                </Button>
            </div>
        );
    };

    const renderNameAndAddressCode = () => {
        return (
            <div className={classes.addressRow}>
                {renderAddressCode()}
                {renderName()}
            </div>
        );
    };

    const renderCompanyName = () => {
        return (
            <div className={classes.addressRow}>
                {renderClientName()}
            </div>
        );
    };

    const renderAddressType = () => {
        if (!showAddressType) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('address_type')}
                </span>
                <Form.Item name="addressType">
                    <Space direction="horizontal">
                        <Radio.Group
                            disabled={editData?.id}
                            className={classes.newAddressInput}
                            options={[
                                { label: t('domestic'), value: false },
                                { label: t('international'), value: true },
                            ]}
                            value={isInternational}
                            onChange={(e) => setIsInternational(e.target.value)}
                        />
                    </Space>
                </Form.Item>
            </div>
        );
    };

    const renderLatLng = () => {
        const showLatitude = isFieldVisibleInForm('latitude');
        const showLongitude = isFieldVisibleInForm('longitude');
        if (!showLatitude && !showLongitude) return null;
        return (
            <div className={classes.addressRow} style={{ alignItems: 'end' }}>
                {showLatitude && (
                    <div className={classes.newAddressItem}>
                        <span className={classes.newAddressTitle}>
                            {t('latitude')}
                            {consignmentValidator.latitude?.required ? '*' : ''}
                        </span>
                        <Input
                            value={newAddress.latitude}
                            className={classes.newAddressInput}
                            placeholder={t('latitude')}
                            onChange={(e) => updateAddressFieldWithValidations('latitude', e.target.value)}
                        />
                    </div>
                )}
                {showLongitude && (
                    <div className={classes.newAddressItem}>
                        <span className={classes.newAddressTitle}>
                            {t('longitude')}
                            {consignmentValidator.longitude?.required ? '*' : ''}
                        </span>
                        <Input
                            value={newAddress.longitude}
                            className={classes.newAddressInput}
                            placeholder={t('longitude')}
                            onChange={(e) => updateAddressFieldWithValidations('longitude', e.target.value)}
                        />
                    </div>
                )}
                {(showLatitude || showLongitude) && (
                    <div
                        onClick={() => setShowMapForLocation(true)}
                        style={{
                            color: '#065492',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            paddingBottom: '4px',
                        }}
                    >
                        <Tooltip title={t('Select on Map')}>
                            <EnvironmentOutlined style={{ fontSize: '24px' }} />
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    };

    const renderMapForLocation = () => {
        if (showMapForLocation) {
            const searchText = newAddress.address_line_1 || newAddress.address_line_2;
            return (
                <MapLocationCapture
                    search={searchText}
                    isVisible={showMapForLocation}
                    onClose={() => setShowMapForLocation(false)}
                    onSubmit={(val: any) => {
                        setNewAddress({
                            ...newAddress,
                            latitude: val.lat,
                            longitude: val.lng,
                            address_line_1: val.geocodedAddress || newAddress.address_line_1,
                        });
                        setShowMapForLocation(false);
                    }}
                    defaultLocation={{
                        lat: newAddress.latitude ? +newAddress.latitude : null,
                        lng: newAddress.longitude ? +newAddress.longitude : null,
                    }}
                />
            );
        }

        return <></>;
    };

    const renderGraceTime = () => {
        if (!isFieldVisibleInForm('grace_time')) return null;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {`${t('graceTime')} (sec)`}
                    {consignmentValidator.grace_time?.required ? '*' : ''}
                </span>
                <GraceTimeInput
                    value={typeof newAddress.graceTime === 'string' ? undefined : newAddress.graceTime}
                    onChange={(val) => setNewAddress({
                        ...newAddress,
                        graceTime: val === undefined || val === null ? '' : val,
                    })}
                    placeholder={t('enterSecondsGreaterThanZero')}
                />
            </div>
        );
    };

    const renderDockCapacity = () => {
        if (!isFieldVisibleInForm('dock_capacity')) return null;
        const dockCapacityNum = typeof newAddress.dockCapacity === 'string'
            ? parseFloat(newAddress.dockCapacity)
            : newAddress.dockCapacity;
        const isInvalidDockCapacity = dockCapacityNum !== undefined
            && dockCapacityNum !== null
            && !Number.isNaN(dockCapacityNum)
            && dockCapacityNum <= 0;
        return (
            <div className={classes.newAddressItem}>
                <span className={classes.newAddressTitle}>
                    {t('dockCapacity')}
                    {consignmentValidator.dock_capacity?.required ? '*' : ''}
                </span>
                <InputNumber
                    value={typeof newAddress.dockCapacity === 'string' ? undefined : newAddress.dockCapacity}
                    style={{ width: '100%' }}
                    placeholder={t('dockCapacity')}
                    status={isInvalidDockCapacity ? 'error' : undefined}
                    onChange={(val) => setNewAddress({
                        ...newAddress,
                        dockCapacity: val === undefined || val === null ? '' : val,
                    })}
                />
                {isInvalidDockCapacity && (
                    <span style={{ color: '#ff4d4f', fontSize: 12 }}>
                        {t('dockCapacityMustBePositive')}
                    </span>
                )}
            </div>
        );
    };

    const computeAggregateTimeSlot = (mapping: DockMappingValue): [any, any] | null => {
        const entries = Object.values(mapping);
        const starts = entries.map((e) => e.startTime).filter(Boolean) as string[];
        const ends = entries.map((e) => e.endTime).filter(Boolean) as string[];
        if (!starts.length || !ends.length) return null;
        const minStart = starts.reduce((a, b) => (a < b ? a : b));
        const maxEnd = ends.reduce((a, b) => (a > b ? a : b));
        return [moment(minStart, 'HH:mm'), moment(maxEnd, 'HH:mm')];
    };

    const renderDockVehicleMapping = () => {
        if (!isFieldVisibleInForm('dock_vehicle_mapping')) return null;

        if (isVehicleMakeLoading) {
            return (
                <div className={classes.newAddressItem} style={{ width: '100%', marginTop: 16 }}>
                    <Spin />
                </div>
            );
        }

        const mappingValue: DockMappingValue = newAddress.dockVehicleMapping || {};

        const handleMappingChange = (val: DockMappingValue) => {
            const updatedMapping = Object.keys(val).length > 0 ? val : undefined;
            setNewAddress({ ...newAddress, dockVehicleMapping: updatedMapping });
            if (updatedMapping) {
                const computed = computeAggregateTimeSlot(updatedMapping);
                setTimeSlotKeys([0]);
                setTimeSlotValues(computed ? { 0: computed } : {});
            } else {
                setTimeSlotKeys([0]);
                setTimeSlotValues({});
            }
        };

        return (
            <div style={{ width: '100%', marginTop: 16, marginBottom: 16 }}>
                <span className={classes.newAddressTitle}>
                    {t('dockToVehicleMapping')}
                    {consignmentValidator.dock_vehicle_mapping?.required ? '*' : ''}
                </span>
                <DockVehicleMapping
                    dockCapacity={Number(newAddress.dockCapacity) || 0}
                    value={mappingValue}
                    onChange={handleMappingChange}
                    vehicleMakeOptions={vehicleMakeOptions}
                />
            </div>
        );
    };

    const renderDockConfiguration = () => {
        const hasAnyDockField = isFieldVisibleInForm('grace_time')
            || isFieldVisibleInForm('dock_capacity')
            || isFieldVisibleInForm('dock_vehicle_mapping');
        if (!hasAnyDockField) return null;
        return (
            <>
                <div className={classes.addressRow}>
                    {renderGraceTime()}
                    {renderDockCapacity()}
                </div>
                {renderDockVehicleMapping()}
            </>
        );
    };

    const renderEmailAndLocationId = () => {
        return (
            <div className={classes.addressRow}>
                { renderEmail() }
                {enableAsn ? renderLocationId() : null}
            </div>
        );
    };

    return (
        <div className={classes.newAddress}>
            {!showSaveButtonAtBottom && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid #EDEDED',
                    }}
                >
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{isEditingPage() ? 'Edit Address' : 'Add Address'}</div>
                    {renderSubmit()}
                </div>
            )}
            <div>
                <Form form={form}>
                    {renderNameAndAddressCode()}
                    {renderCompanyName()}
                    {renderAddressType()}
                    {renderAddressRow()}
                    {renderNationalAddressCode()}
                    {renderLocality()}
                    {renderWhat3Word()}
                    {renderLatLng()}
                    {renderMapForLocation()}
                    {renderPhoneNumbers()}
                    {renderServiceTimeAndLocationImage()}
                    {renderEmailAndLocationId()}
                    {renderTimeSlot()}
                    {renderDockConfiguration()}
                </Form>
            </div>
            {showSaveButtonAtBottom && renderSubmit()}
            {loading && <Loader zIndex={10} />}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    return {
        config: state.master.config,
        partsToShow: state?.master?.parts_to_show,
        showAddressType: state?.master?.parts_to_show?.allow_international_booking,
        customerPortalConfig: state?.master?.config?.customer_portal_config,
        locationMasterConfig: state?.master?.config?.customer_address_book_config,
    };
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

const CreateAddressForm = GenericHoc(hocConfig)(CreateAddress);

export default CreateAddressForm;
